import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../pages/auth/authSlice";
import { useGetSubscriptionQuery } from "../../app/subscriptionApiSlice";

export default function SubscriptionBanner() {
  const navigate = useNavigate();
  const theme = useTheme();
  const user = useSelector(selectCurrentUser);

  const isAuthorized = user?.roles?.some(
    (r) =>
      r.role?.name === "admin" ||
      r.role?.name === "accountant" ||
      r === "admin" ||
      r === "accountant"
  );

  const { data } = useGetSubscriptionQuery(undefined, {
    skip: !user || !isAuthorized,
  });

  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem("dismissed_subscription_banner") === "true",
  );

  const sub = data?.subscription;
  const paygBalance = data?.payg_balance;

  const rawStatus = sub?.status || "inactive";
  // "Expired" only means something for a school actually on subscription
  // billing (a real paid plan). A free-plan or pay-as-you-go school can end
  // up with status "cancelled" too (e.g. after a lapsed grace period, or
  // switching to parent-pays with no paid plan to preserve) — that's not a
  // subscription expiring, so it must never show this message.
  const hasPaidPlan = Boolean(sub?.plan) && sub.plan !== "free";
  const isExpired =
    hasPaidPlan &&
    (sub?.period_end
      ? new Date(sub.period_end) < new Date()
      : ["expired", "cancelled"].includes(rawStatus));

  const subscriptionIsActive =
    hasPaidPlan &&
    ["active", "trial"].includes(rawStatus) &&
    !isExpired;

  // PAYG alert is only evaluated if there is NO active paid subscription
  const isLowCredit =
    !subscriptionIsActive &&
    !isExpired &&
    paygBalance !== undefined &&
    paygBalance !== null &&
    paygBalance <= 20;

  const isCritical = isExpired || (!subscriptionIsActive && paygBalance === 0);

  // Reset dismissal if state transitions to critical
  useEffect(() => {
    if (
      isCritical &&
      localStorage.getItem("dismissed_subscription_banner") === "true"
    ) {
      localStorage.removeItem("dismissed_subscription_banner");
      setDismissed(false);
    }
  }, [isCritical]);

  if (!user || !isAuthorized || (!isExpired && !isLowCredit) || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem("dismissed_subscription_banner", "true");
    setDismissed(true);
  };

  const getMessage = () => {
    if (isExpired) {
      return "Your subscription plan has expired. Renew your plan to avoid disruptions.";
    }
    if (paygBalance === 0) {
      return "Your school has 0 credits left. Subscribe to a plan or top up credits to avoid disruptions.";
    }
    return `Notice: Credit balance is running low (${paygBalance} remaining). Subscribe to a plan or top up credits to avoid disruptions.`;
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1, pb: 0.5 }}>
      <Alert
        severity={isCritical ? "error" : "warning"}
        icon={isCritical ? <ErrorOutlineIcon /> : <WarningAmberIcon />}
        sx={{
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 4px 12px rgba(0, 0, 0, 0.4)"
              : "0 2px 8px rgba(0, 0, 0, 0.05)",
          "& .MuiAlert-message": {
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            width: "100%",
            justifyContent: "space-between",
          },
        }}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={handleDismiss}
            title="Dismiss warning"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, flex: 1, minWidth: 220 }}
        >
          {getMessage()}
        </Typography>

        <Button
          variant="contained"
          size="small"
          onClick={() => navigate("/subscription")}
          color={isCritical ? "error" : "warning"}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "12px",
            px: 2,
            py: 0.4,
            whiteSpace: "nowrap",
            boxShadow: "none",
          }}
        >
          {isExpired ? "Renew Plan" : "Subscribe or Top Up"}
        </Button>
      </Alert>
    </Box>
  );
}
